import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  ConfigProvider,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Skeleton,
  Switch,
  Tag,
} from 'antd';
import {
  AlertOutlined,
  AppstoreOutlined,
  DashboardOutlined,
  DollarOutlined,
  ContactsOutlined,
  LogoutOutlined,
  LinkOutlined,
  LineChartOutlined,
  ReloadOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  LoginFormPage,
  PageContainer,
  ProCard,
  ProFormText,
  ProLayout,
  ProTable,
  StatisticCard,
} from '@ant-design/pro-components';
import type { ProColumns } from '@ant-design/pro-components';
import axios from 'axios';
import {
  AdminSession,
  AnalysisRunCost,
  AnalysisUsageDetail,
  AdminUserRow,
  ConsultationLead,
  ConsultationStatus,
  AnalyticsEvent,
  AppleFinanceData,
  AppleOrderLookupResult,
  AppleOrderLookupTransaction,
  AppleReconciliationIssue,
  AppleTransaction,
  DashboardData,
  DeepSeekBillingData,
  FeatureFlag,
  RuntimeSettingSummary,
  adminLogin,
  fetchAdminDashboard,
  fetchDeepSeekBilling,
  fetchAdminUsers,
  fetchConsultationLeads,
  fetchAnalyticsEvents,
  fetchAnalysisRunDetail,
  fetchAnalysisRuns,
  fetchAppleFinance,
  fetchAppleReconciliationIssues,
  fetchAppleTransactions,
  fetchFeatureFlags,
  fetchRuntimeSettings,
  grantAdminUserCredits,
  setAdminUserGiftPlus,
  restoreRuntimeSetting,
  refreshAppleTransactions,
  lookupAppleOrder,
  reconcileAppleOrder,
  saveAppleSettlement,
  updateFeatureFlag,
  updateAdminUserStatus,
  updateConsultationLeadStatus,
  updateRuntimeSetting,
} from './adminApi';
import './admin.css';

const SESSION_KEY = 'guanzhai_admin_session';

type Section = 'overview' | 'analysis-costs' | 'orders' | 'finance' | 'features' | 'users' | 'consultations' | 'events' | 'settings';

const sectionMeta: Record<Section, { title: string; description: string }> = {
  overview: { title: '系统概览', description: '查看近 7 天用户、分析请求和运行事件。' },
  'analysis-costs': { title: '分析成本', description: '按单次分析查看识别、报告的 Token、API 耗时和估算成本。' },
  orders: { title: '订单管理', description: '查看 App Store 购买、订阅、续订与退款状态。' },
  finance: { title: '消费与入账', description: '核对 App Store 交易、退款和财务结算记录。' },
  features: { title: '功能模块', description: '控制客户端功能是否对用户开放。' },
  users: { title: '用户数据', description: '管理使用次数、账号状态和用户档案。' },
  consultations: { title: '咨询线索', description: '查看用户提交的专业风水咨询需求并跟进转化状态。' },
  events: { title: '事件监测', description: '查看用户登录和分析运行事件。' },
  settings: { title: '系统设置', description: '管理运行时业务密钥与服务配置。' },
};

const proRoutes = [
  { path: '/overview', name: '系统概览', icon: <DashboardOutlined /> },
  { path: '/analysis-costs', name: '分析成本', icon: <LineChartOutlined /> },
  { path: '/orders', name: '订单管理', icon: <ShoppingCartOutlined /> },
  { path: '/finance', name: '消费与入账', icon: <DollarOutlined /> },
  { path: '/features', name: '功能模块', icon: <AppstoreOutlined /> },
  { path: '/users', name: '用户数据', icon: <TeamOutlined /> },
  { path: '/consultations', name: '咨询线索', icon: <ContactsOutlined /> },
  { path: '/events', name: '事件监测', icon: <AlertOutlined /> },
  { path: '/settings', name: '系统设置', icon: <SettingOutlined /> },
];

const isSection = (value: string): value is Section => value in sectionMeta;

const eventLabels: Record<string, string> = {
  'admin.login': '管理员登录',
  'admin.feature.updated': '功能开关变更',
  'user.login': '用户登录',
  'analysis.floorplan': '户型识别',
  'analysis.report': '报告生成',
  'analysis.airflow': '气流分析',
  'admin.setting.updated': '运行配置更新',
  'admin.setting.restored': '运行配置恢复',
  'admin.apple.settlement.updated': 'App Store 结算更新',
  'admin.apple.transactions.refreshed': 'App Store 订单刷新',
  'admin.apple.order.reconciled': 'Apple Order ID 人工补单',
  'admin.user.credits.granted': '管理员增加使用次数',
  'admin.user.credits.reduced': '管理员减少使用次数',
  'admin.user.plus.granted': '管理员赠送 Plus',
  'admin.user.plus.revoked': '管理员撤销赠送 Plus',
  'admin.user.disabled': '管理员禁用用户',
  'admin.user.enabled': '管理员恢复用户',
  'admin.consultation.status.updated': '咨询线索状态更新',
};

const consultationStatusMeta: Record<ConsultationStatus, { label: string; color: string }> = {
  new: { label: '新线索', color: 'error' },
  contacted: { label: '已联系', color: 'processing' },
  following_up: { label: '跟进中', color: 'warning' },
  converted: { label: '已成交', color: 'success' },
  closed: { label: '已关闭', color: 'default' },
};

const formatDate = (value?: string) => value
  ? new Intl.DateTimeFormat('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
  : '—';

const userSourceLabels: Record<string, string> = {
  phone: '手机号', apple: 'Apple', google: 'Google', line: 'LINE', email: '邮箱',
};
const userRegionLabels: Record<string, string> = { CN: '中国', JP: '日本', US: '美国' };

const readSession = (): AdminSession | null => {
  try {
    const session = JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null') as AdminSession | null;
    return session && Date.parse(session.expiresAt) > Date.now() ? session : null;
  } catch {
    return null;
  }
};

function AdminLogin({ onSuccess }: { onSuccess: (session: AdminSession) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (values: { username: string; password: string }) => {
    setSubmitting(true);
    setError('');
    try {
      const session = await adminLogin(values.username, values.password);
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      onSuccess(session);
      return true;
    } catch (requestError) {
      const message = axios.isAxiosError(requestError)
        ? requestError.response?.data?.message ?? '登录失败，请检查管理员配置'
        : '登录失败，请稍后重试';
      setError(String(message));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="admin-pro-login">
      <LoginFormPage
        logo={<span className="admin-pro-logo">宅</span>}
        title="观宅运营控制台"
        subTitle="独立于用户端的系统运营与风险控制后台"
        activityConfig={{
          title: <div className="admin-login-hero-title">看清运行状态，<br />再决定开放什么。</div>,
          subTitle: <div className="admin-login-hero-copy">集中管理功能开放、用户权益、App Store 订单与服务配置。每次关键操作都有审计记录。</div>,
          action: (
            <div className="admin-login-signals" aria-label="后台管理能力">
              <span><b>01</b> 订单与权益对账</span>
              <span><b>02</b> 用户与功能控制</span>
              <span><b>03</b> 事件与配置审计</span>
            </div>
          ),
        }}
        onFinish={submit}
        submitter={{ searchConfig: { submitText: '进入控制台' }, submitButtonProps: { loading: submitting, size: 'large' } }}
      >
        {error ? <Alert className="admin-login-alert" type="error" message={error} showIcon /> : null}
        <ProFormText name="username" label="管理员账号" fieldProps={{ autoComplete: 'username', size: 'large' }} placeholder="ADMIN_USERNAME" rules={[{ required: true, message: '请输入管理员账号' }]} />
        <ProFormText.Password name="password" label="管理员密码" fieldProps={{ autoComplete: 'current-password', size: 'large' }} placeholder="ADMIN_PASSWORD" rules={[{ required: true, message: '请输入管理员密码' }]} />
        <div className="admin-login-footnote">管理员登录、配置修改和用户操作都会写入审计事件。</div>
      </LoginFormPage>
    </main>
  );
}

function TrendChart({ data }: { data: DashboardData['trend'] }) {
  const max = Math.max(...data.map(item => item.total), 1);
  if (!data.length) return <div className="admin-empty">统计事件产生后，这里会显示每日趋势。</div>;
  return (
    <div className="admin-trend" aria-label="每日事件趋势">
      {data.map(item => (
        <div className="admin-trend-day" key={item.date}>
          <div className="admin-trend-bars" title={`${item.date}：${item.total} 次`}>
            <span className="admin-trend-success" style={{ transform: `scaleY(${item.successful / max})` }} />
            <span className="admin-trend-failed" style={{ transform: `scaleY(${item.failed / max})` }} />
          </div>
          <b>{item.total}</b>
          <small>{item.date.slice(5)}</small>
        </div>
      ))}
    </div>
  );
}

function Overview({ dashboard }: { dashboard: DashboardData }) {
  return (
    <div className="admin-section-stack">
      <ProCard className="admin-stat-grid" gutter={16} wrap>
        <StatisticCard statistic={{ title: '分析请求', value: dashboard.analyses.total, description: `${dashboard.analyses.successful} 成功 / ${dashboard.analyses.failed} 异常` }} />
        <StatisticCard statistic={{ title: '事件成功率', value: dashboard.events.successRate, precision: 1, suffix: '%', description: `共 ${dashboard.events.total} 条运行事件` }} />
        <StatisticCard statistic={{ title: '注册用户', value: dashboard.users.total, description: `今日活跃 ${dashboard.users.activeToday}` }} />
        <StatisticCard statistic={{ title: '开放模块', value: dashboard.enabledFeatureCount, suffix: `/ ${dashboard.totalFeatureCount}`, description: '服务端实时控制' }} />
      </ProCard>
      <ProCard title="首版付费与咨询漏斗" className="admin-stat-grid" gutter={16} wrap>
        <StatisticCard statistic={{ title: '报告完成', value: dashboard.conversion.reportsCompleted, description: `转化区曝光 ${dashboard.conversion.conversionPanelsViewed}` }} />
        <StatisticCard statistic={{ title: '个人匹配点击', value: dashboard.conversion.personalCtaClicks, description: `购买页到达 ${dashboard.conversion.billingViews}` }} />
        <StatisticCard statistic={{ title: '发起购买', value: dashboard.conversion.purchaseStarts, description: `验签成功 ${dashboard.conversion.purchasesVerified}` }} />
        <StatisticCard statistic={{ title: '个人匹配完成', value: dashboard.conversion.personalMatches, description: '年度 Plus 使用结果' }} />
        <StatisticCard statistic={{ title: '咨询入口点击', value: dashboard.conversion.consultationClicks, description: `成功线索 ${dashboard.conversion.consultationLeads}` }} />
      </ProCard>
      <ProCard title="运行趋势" extra={`近 ${dashboard.periodDays} 天 · 平均响应 ${dashboard.events.averageDurationMs} ms`} className="admin-trend-card">
        <TrendChart data={dashboard.trend} />
      </ProCard>
    </div>
  );
}

function FeaturePanel({ features, updating, onToggle }: {
  features: FeatureFlag[];
  updating: string | null;
  onToggle: (feature: FeatureFlag, enabled: boolean) => void;
}) {
  return (
    <div className="admin-section-stack">
      <Alert className="admin-note" type="warning" showIcon={false} message="关闭模块会立即影响正在使用 App 和 Web 的用户，请先确认当前业务状态。" />
      <ProCard className="admin-feature-list" title="服务端实时开关" extra="关闭后相关 API 返回 503">
        {features.map((feature, index) => (
          <article className="admin-feature-row" key={feature.key}>
            <span className="admin-feature-number">{String(index + 1).padStart(2, '0')}</span>
            <div><h2>{feature.name}</h2><p>{feature.description}</p><small>上次更新：{formatDate(feature.updatedAt)} · {feature.updatedBy}</small></div>
            <div className="admin-feature-control">
              <Tag color={feature.enabled ? 'success' : 'default'}>{feature.enabled ? '运行中' : '已关闭'}</Tag>
              {feature.enabled ? (
                <Popconfirm
                  title={`确认关闭${feature.name}？`}
                  description="相关客户端请求会立即停止。"
                  okText="确认关闭"
                  cancelText="取消"
                  onConfirm={() => onToggle(feature, false)}
                >
                  <Switch checked loading={updating === feature.key} aria-label={`关闭${feature.name}`} />
                </Popconfirm>
              ) : (
                <Switch checked={false} loading={updating === feature.key} onChange={() => onToggle(feature, true)} aria-label={`开启${feature.name}`} />
              )}
            </div>
          </article>
        ))}
      </ProCard>
    </div>
  );
}

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('zh-CN', { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

function FinancePanel({ finance, onSaveSettlement }: {
  finance: AppleFinanceData;
  onSaveSettlement: (values: {
    fiscalPeriod: string;
    currency: string;
    proceedsAmount: number;
    paidAmount?: number;
  }) => Promise<void>;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const settlementColumns: ProColumns<AppleFinanceData['settlements'][number]>[] = [
    { title: '财务月份', dataIndex: 'fiscalPeriod' },
    { title: '币种', dataIndex: 'currency' },
    { title: '最终收益', key: 'proceeds', render: (_: unknown, row: AppleFinanceData['settlements'][number]) => formatMoney(row.proceedsAmount, row.currency) },
    { title: '已入账', key: 'paid', render: (_: unknown, row: AppleFinanceData['settlements'][number]) => formatMoney(row.paidAmount, row.currency) },
    { title: '状态', dataIndex: 'status', render: (_, row) => <Tag color={row.status === 'paid' ? 'success' : 'warning'}>{row.status === 'paid' ? '已入账' : '待入账'}</Tag> },
    { title: '来源', dataIndex: 'source', render: (_, row) => row.source === 'manual' ? '人工录入' : '财务报告' },
  ];
  const submit = async (values: { fiscalPeriod: string; currency: string; proceedsAmount: number; paidAmount?: number }) => {
    setSaving(true);
    try {
      await onSaveSettlement({ ...values, currency: values.currency.toUpperCase() });
      form.resetFields();
    } finally { setSaving(false); }
  };
  return (
    <div className="admin-section-stack">
      <section className="admin-finance-metrics">
        <div><span>全部交易</span><strong>{finance.transactions.total}</strong><small>生产 {finance.transactions.production} / 沙盒 {finance.transactions.sandbox}</small></div>
        <div><span>退款交易</span><strong>{finance.transactions.refunded}</strong><small>按服务器交易状态统计</small></div>
        <div className="admin-finance-currencies"><span>生产订单金额</span>{finance.transactions.byCurrency.length ? finance.transactions.byCurrency.map(item => <strong key={item.currency}>{formatMoney(item.grossAmount, item.currency)}</strong>) : <strong>暂无金额</strong>}<small>不作为财务确认依据</small></div>
        <div className="admin-finance-currencies"><span>已结算入账</span>{finance.settlements.length ? finance.settlements.slice(0, 2).map(item => <strong key={`${item.fiscalPeriod}-${item.currency}`}>{formatMoney(item.paidAmount, item.currency)}</strong>) : <strong>尚未录入</strong>}<small>按苹果财务月份记录</small></div>
      </section>
      <ProCard title="苹果财务结算" extra="FINAL PROCEEDS">
        <Alert className="admin-finance-note" type="info" showIcon={false} message="交易 JWS 的 price/currency 不能用于收入确认。这里的最终收益与已入账金额应来自 App Store Connect 财务报告。" />
        <Form form={form} layout="inline" className="admin-settlement-form" onFinish={submit} initialValues={{ currency: 'CNY' }}>
          <Form.Item name="fiscalPeriod" label="财务月份" rules={[{ required: true, pattern: /^\d{4}-\d{2}$/, message: '格式 YYYY-MM' }]}><Input placeholder="2026-08" /></Form.Item>
          <Form.Item name="currency" label="币种" rules={[{ required: true, pattern: /^[A-Za-z]{3}$/, message: '三位币种代码' }]}><Input placeholder="CNY" maxLength={3} /></Form.Item>
          <Form.Item name="proceedsAmount" label="最终收益" rules={[{ required: true, message: '请输入最终收益' }]}><InputNumber min={0} precision={2} /></Form.Item>
          <Form.Item name="paidAmount" label="已入账"><InputNumber min={0} precision={2} /></Form.Item>
          <Button htmlType="submit" type="primary" loading={saving}>记录结算</Button>
        </Form>
        <ProTable rowKey={row => `${row.fiscalPeriod}-${row.currency}`} columns={settlementColumns} dataSource={finance.settlements} search={false} options={false} pagination={false} scroll={{ x: 720 }} />
      </ProCard>
    </div>
  );
}

function AppleOrderRecoveryPanel({ users, onLookup, onReconcile }: {
  users: AdminUserRow[];
  onLookup: (orderId: string) => Promise<AppleOrderLookupResult>;
  onReconcile: (input: {
    orderId: string;
    transactionId: string;
    userId?: string;
    confirmManualAssignment?: boolean;
  }) => Promise<AppleOrderLookupResult>;
}) {
  const [orderId, setOrderId] = useState('');
  const [result, setResult] = useState<AppleOrderLookupResult | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Record<string, string>>({});
  const [querying, setQuerying] = useState(false);
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const userOptions = useMemo(() => users.map(user => ({
    value: user.id,
    label: `${user.displayName} · ${user.phoneNumber}`,
  })), [users]);

  const query = async () => {
    const value = orderId.trim();
    if (!value) return;
    setQuerying(true);
    setFeedback(null);
    try {
      const next = await onLookup(value);
      setResult(next);
      setOrderId(next.orderId);
      setFeedback({ type: 'success', text: `Apple 返回 ${next.transactions.length} 笔交易，请确认归属后补单。` });
    } catch (error) {
      const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined;
      setResult(null);
      setFeedback({ type: 'error', text: String(message ?? 'Apple Order ID 查询失败。') });
    } finally {
      setQuerying(false);
    }
  };

  const apply = async (transaction: AppleOrderLookupTransaction) => {
    const selectedUserId = transaction.matchedUserId ?? selectedUsers[transaction.transactionId];
    setReconcilingId(transaction.transactionId);
    setFeedback(null);
    try {
      const next = await onReconcile({
        orderId: result!.orderId,
        transactionId: transaction.transactionId,
        ...(selectedUserId ? { userId: selectedUserId } : {}),
        confirmManualAssignment: !transaction.matchedUserId,
      });
      setResult(next);
      setFeedback({ type: 'success', text: transaction.alreadyRecorded ? '订单已存在，未重复发放权益。' : '订单已验证并补发权益。' });
    } catch (error) {
      const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined;
      setFeedback({ type: 'error', text: String(message ?? '订单补发失败。') });
    } finally {
      setReconcilingId(null);
    }
  };

  const columns: ProColumns<AppleOrderLookupTransaction>[] = [
    { title: '交易号', dataIndex: 'transactionId', width: 190, copyable: true, render: value => <span className="admin-mono">{String(value)}</span> },
    { title: '商品', dataIndex: 'productId', width: 220, render: value => <span className="admin-mono">{String(value)}</span> },
    { title: '金额', width: 110, render: (_, row) => row.priceMilliunits && row.currency ? formatMoney((row.priceMilliunits * row.quantity) / 1000, row.currency) : '未提供' },
    { title: '购买时间', dataIndex: 'purchasedAt', width: 175, render: value => formatDate(String(value)) },
    {
      title: '用户归属',
      width: 260,
      render: (_, row) => row.matchedUserId ? (
        <div><b>{row.matchedUserDisplayName ?? row.matchedUserId}</b><small className="admin-table-sub">{row.matchedUserPhoneNumber ?? row.matchedUserId}</small></div>
      ) : row.hasAppAccountToken ? (
        <Tag color="error">令牌对应用户不存在</Tag>
      ) : (
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="选择要绑定的用户"
          options={userOptions}
          value={selectedUsers[row.transactionId]}
          onChange={value => setSelectedUsers(current => ({ ...current, [row.transactionId]: value }))}
          style={{ width: 240 }}
        />
      ),
    },
    { title: '校验', width: 150, render: (_, row) => row.validationError ? <Tag color="error">{row.validationError}</Tag> : row.alreadyRecorded ? <Tag color="success">已入库</Tag> : <Tag color="processing">可补单</Tag> },
    {
      title: '操作',
      width: 130,
      fixed: 'right',
      render: (_, row) => {
        if (row.alreadyRecorded) return <Button disabled>已处理</Button>;
        const selectedUserId = row.matchedUserId ?? selectedUsers[row.transactionId];
        const disabled = !row.eligible || !selectedUserId || (row.hasAppAccountToken && !row.matchedUserId);
        return (
          <Popconfirm
            title={row.matchedUserId ? '确认按交易中的账号补发权益？' : '该交易没有账号令牌，确认人工绑定给所选用户？'}
            description="操作会写入审计记录；同一交易号不会重复增加权益。"
            okText="确认补单"
            cancelText="取消"
            disabled={disabled}
            onConfirm={() => apply(row)}
          >
            <Button type="primary" danger={!row.matchedUserId} disabled={disabled} loading={reconcilingId === row.transactionId}>补发权益</Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <ProCard
      title="Apple Order ID 人工找单"
      extra={<Button type="link" icon={<LinkOutlined />} href="https://developer.apple.com/documentation/appstoreserverapi/look-up-order-id" target="_blank">Apple 查询接口文档</Button>}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert type="warning" showIcon message="仅支持正式环境订单。先查询并核对商品、时间和账号归属，再执行补单；Sandbox Order ID 无法查询。" />
        <Input.Search
          value={orderId}
          onChange={event => setOrderId(event.target.value)}
          onSearch={() => void query()}
          enterButton="查询 Apple 订单"
          loading={querying}
          placeholder="输入 Apple 邮件收据中的 Order ID"
          allowClear
        />
        {feedback ? <Alert type={feedback.type} showIcon message={feedback.text} /> : null}
        {result ? <ProTable<AppleOrderLookupTransaction>
          headerTitle={`${result.orderId} · 正式环境`}
          rowKey="transactionId"
          columns={columns}
          dataSource={result.transactions}
          search={false}
          options={false}
          pagination={false}
          scroll={{ x: 1280 }}
        /> : null}
      </Space>
    </ProCard>
  );
}

function OrdersPanel({ transactions, issues, users, refreshing, onRefresh, onLookupOrder, onReconcileOrder }: {
  transactions: AppleTransaction[];
  issues: AppleReconciliationIssue[];
  users: AdminUserRow[];
  refreshing: boolean;
  onRefresh: () => Promise<void>;
  onLookupOrder: (orderId: string) => Promise<AppleOrderLookupResult>;
  onReconcileOrder: (input: {
    orderId: string;
    transactionId: string;
    userId?: string;
    confirmManualAssignment?: boolean;
  }) => Promise<AppleOrderLookupResult>;
}) {
  const productMeta: Record<string, { name: string; type: string; color?: string }> = {
    'com.guanzhai.ai.analysis.single': { name: '单次完整分析', type: '单次分析' },
    'com.guanzhai.ai.analysis.family': { name: '家庭购房包', type: '次数包' },
    'com.guanzhai.ai.plus': { name: '观宅 Plus 年度会员', type: '自动续订年费', color: 'processing' },
    'com.guanzhai.ai.superplus': { name: '历史观宅超级 Plus', type: '历史永久权益', color: 'gold' },
    'com.guanzhai.ai.plus.monthly': { name: '旧版月度 Plus', type: '旧版订阅', color: 'processing' },
    'com.guanzhai.ai.credits10': { name: '旧版分析补充包', type: '旧版次数包' },
  };
  const purchased = transactions.filter(item => item.status !== 'refunded').length;
  const refunded = transactions.filter(item => item.status === 'refunded').length;
  const subscriptions = transactions.filter(item => Boolean(item.expiresAt)).length;
  const pendingIntents = issues.filter(item => item.kind === 'intent').length;
  const unmatchedNotifications = issues.filter(item => item.kind === 'notification').length;
  const orderMetrics = [
    { label: '全部订单', value: transactions.length },
    { label: '有效支付', value: purchased },
    { label: '订阅交易', value: subscriptions },
    { label: '退款撤销', value: refunded },
    { label: '购买待确认', value: pendingIntents, attention: pendingIntents > 0 },
    { label: '未匹配通知', value: unmatchedNotifications, attention: unmatchedNotifications > 0 },
  ];
  const columns: ProColumns<AppleTransaction>[] = [
    { title: '订单号', dataIndex: 'transactionId', width: 190, copyable: true, render: (_, row) => <span className="admin-mono">{row.transactionId}</span> },
    { title: '用户', dataIndex: 'userDisplayName', width: 180, render: (_, row) => <div><b>{row.userDisplayName ?? row.userId}</b>{row.userPhoneNumber ? <small className="admin-table-sub">{row.userPhoneNumber}</small> : null}</div> },
    { title: '类型', key: 'type', width: 105, render: (_, row) => <Tag color={productMeta[row.productId]?.color ?? (row.expiresAt ? 'processing' : 'default')}>{productMeta[row.productId]?.type ?? (row.expiresAt ? '订阅' : '一次性商品')}</Tag> },
    { title: '商品', dataIndex: 'productId', width: 250, render: (_, row) => <div><b>{productMeta[row.productId]?.name ?? '未知商品'}</b><small className="admin-table-sub admin-mono">{row.productId}</small></div> },
    { title: '金额', key: 'amount', width: 115, render: (_, row) => row.priceMilliunits && row.currency ? formatMoney((row.priceMilliunits * (row.quantity || 1)) / 1000, row.currency) : '待同步' },
    { title: '环境', dataIndex: 'environment', width: 110, render: (_, row) => <Tag color={row.environment === 'Production' ? 'success' : 'default'}>{row.environment === 'Production' ? '生产' : '沙盒'}</Tag> },
    { title: '入库来源', dataIndex: 'source', width: 130, render: (_, row) => <Tag>{row.source === 'app' ? 'App 上报' : row.source === 'scheduled' ? '定时对账' : row.source === 'notification-history' ? '通知补拉' : row.source === 'order-lookup' ? 'Order ID 找单' : 'Apple 通知'}</Tag> },
    { title: '状态', dataIndex: 'status', width: 100, render: (_, row) => <Tag color={row.status === 'refunded' ? 'error' : 'success'}>{row.status === 'refunded' ? '已退款' : '已支付'}</Tag> },
    { title: '购买时间', dataIndex: 'purchasedAt', width: 175, render: (_, row) => formatDate(row.purchasedAt) },
    { title: '订阅到期', dataIndex: 'expiresAt', width: 175, render: (_, row) => formatDate(row.expiresAt) },
    { title: '最近校验', dataIndex: 'lastVerifiedAt', width: 175, render: (_, row) => formatDate(row.lastVerifiedAt) },
  ];
  const issueColumns: ProColumns<AppleReconciliationIssue>[] = [
    { title: '类型', dataIndex: 'kind', width: 110, render: (_, row) => <Tag color={row.kind === 'notification' ? 'warning' : 'processing'}>{row.kind === 'notification' ? '未匹配通知' : '购买待确认'}</Tag> },
    { title: '用户', dataIndex: 'userId', width: 190, copyable: true },
    { title: '商品', dataIndex: 'productId', width: 220, render: value => value ? <span className="admin-mono">{String(value)}</span> : '—' },
    { title: '交易号', dataIndex: 'transactionId', width: 190, copyable: true, render: value => value ? String(value) : '尚未上报' },
    { title: '状态', dataIndex: 'status', width: 100 },
    { title: '原因', dataIndex: 'error', width: 260, render: value => value ? String(value) : '等待 App 或 Apple 通知确认' },
    { title: '创建时间', dataIndex: 'createdAt', width: 175, render: value => formatDate(String(value)) },
  ];
  return (
    <div className="admin-section-stack">
      <AppleOrderRecoveryPanel users={users} onLookup={onLookupOrder} onReconcile={onReconcileOrder} />
      <section className="admin-order-summary" aria-label="订单统计">
        {orderMetrics.map(metric => (
          <div className={metric.attention ? 'is-attention' : undefined} key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
          </div>
        ))}
      </section>
      <Alert type={issues.length ? 'warning' : 'success'} showIcon message={issues.length ? `发现 ${issues.length} 条需要关注的支付对账记录；系统仍会自动重试。` : 'App 上报、Apple 通知历史和已知订单均已纳入自动对账。'} />
      <ProTable<AppleTransaction>
        headerTitle="App Store 用户订单"
        toolBarRender={() => [<Button key="refresh-orders" type="primary" icon={<ReloadOutlined />} loading={refreshing} onClick={() => void onRefresh()}>从 App Store 刷新</Button>]}
        rowKey="transactionId"
        columns={columns}
        dataSource={transactions}
        search={false}
        options={{ reload: false, density: true, setting: true }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 1680 }}
      />
      {issues.length ? <ProTable<AppleReconciliationIssue>
        headerTitle="支付对账异常"
        rowKey={row => `${row.kind}:${row.id}`}
        columns={issueColumns}
        dataSource={issues}
        search={false}
        options={false}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1250 }}
      /> : null}
    </div>
  );
}

const analysisStatusLabels: Record<string, string> = {
  recognition_queued: '识别排队中',
  recognizing: '正在识别',
  awaiting_calibration: '待校准',
  recognition_failed: '识别失败',
  report_queued: '报告排队中',
  reporting: '正在生成报告',
  report_failed: '报告失败',
  completed: '已完成',
};

const creditStatusLabels: Record<string, string> = {
  reserved: '已冻结',
  consumed: '已扣除',
  refunded: '已退回',
  not_charged: '未扣次',
};

const formatEstimatedCost = (micros = 0) => `¥${(micros / 1_000_000).toFixed(4)}`;

function AnalysisCostsPanel({ runs, billing, loadDetail, refreshBilling }: {
  runs: AnalysisRunCost[];
  billing: DeepSeekBillingData | null;
  loadDetail: (id: string) => Promise<AnalysisRunCost>;
  refreshBilling: () => Promise<void>;
}) {
  const [detail, setDetail] = useState<AnalysisRunCost | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const totalCost = runs.reduce((sum, run) => sum + run.estimatedCostMicros, 0);
  const totalTokens = runs.reduce((sum, run) => sum + run.totalTokens, 0);
  const completed = runs.filter(run => run.status === 'completed').length;
  const balanceText = billing?.balances.map(item => {
    try { return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: item.currency }).format(Number(item.totalBalance)); }
    catch { return `${item.currency} ${item.totalBalance}`; }
  }).join(' / ') || '—';

  const refreshOfficialBilling = async () => {
    setBillingLoading(true);
    try { await refreshBilling(); }
    finally { setBillingLoading(false); }
  };

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try { setDetail(await loadDetail(id)); }
    finally { setDetailLoading(false); }
  };

  const columns: ProColumns<AnalysisRunCost>[] = [
    { title: '任务', dataIndex: 'id', width: 150, copyable: true, render: value => <span className="admin-mono">{String(value).slice(0, 8)}</span> },
    { title: '用户', width: 190, render: (_, row) => <div><b>{row.userDisplayName ?? row.userId}</b><small className="admin-table-sub">{row.userPhoneNumber ?? row.userId}</small></div> },
    { title: '状态', width: 130, render: (_, row) => <Tag color={row.status === 'completed' ? 'success' : row.status.includes('failed') ? 'error' : 'processing'}>{analysisStatusLabels[row.status] ?? row.status}</Tag> },
    { title: '扣次', width: 95, render: (_, row) => <Tag color={row.creditStatus === 'refunded' ? 'warning' : row.creditStatus === 'consumed' ? 'success' : 'default'}>{creditStatusLabels[row.creditStatus] ?? row.creditStatus}</Tag> },
    { title: '供应商请求', dataIndex: 'usageCount', width: 110 },
    { title: 'Token', dataIndex: 'totalTokens', width: 120, render: (_, row) => row.totalTokens.toLocaleString('zh-CN') },
    { title: 'API 累计耗时', width: 135, render: (_, row) => `${(row.providerDurationMs / 1000).toFixed(2)} s` },
    { title: '用量计费成本', width: 125, render: (_, row) => <b>{formatEstimatedCost(row.estimatedCostMicros)}</b> },
    { title: '创建时间', width: 175, render: (_, row) => formatDate(row.createdAt) },
    { title: '操作', width: 90, fixed: 'right', render: (_, row) => <Button size="small" loading={detailLoading} onClick={() => void openDetail(row.id)}>明细</Button> },
  ];

  const usageColumns: ProColumns<AnalysisUsageDetail>[] = [
    { title: '阶段', dataIndex: 'stage', width: 90, render: value => value === 'recognition' ? '户型识别' : '报告生成' },
    { title: '供应商/模型', width: 180, render: (_, row) => <div><b>{row.provider}</b><small className="admin-table-sub">{row.model ?? row.operation}</small></div> },
    { title: '调用', dataIndex: 'operation', width: 165, render: value => <span className="admin-mono">{String(value)}</span> },
    { title: '状态', width: 75, render: (_, row) => <Tag color={row.success ? 'success' : 'error'}>{row.success ? '成功' : '失败'}</Tag> },
    { title: '耗时', width: 95, render: (_, row) => `${row.durationMs} ms` },
    { title: '输入 Token', dataIndex: 'inputTokens', width: 105, render: value => Number(value ?? 0).toLocaleString('zh-CN') },
    { title: '缓存命中', dataIndex: 'cachedInputTokens', width: 105, render: value => Number(value ?? 0).toLocaleString('zh-CN') },
    { title: '输出 Token', dataIndex: 'outputTokens', width: 105, render: value => Number(value ?? 0).toLocaleString('zh-CN') },
    { title: '用量计费成本', width: 120, render: (_, row) => formatEstimatedCost(row.estimatedCostMicros) },
    { title: '计费依据', width: 145, render: (_, row) => row.billingSource === 'provider_token_usage' ? '供应商 Token 回传' : '运营配置单次价' },
  ];

  return (
    <div className="admin-section-stack">
      <Alert type="info" showIcon message="DeepSeek 官方只开放实时余额 API，不提供逐笔账单 API。下方余额来自官方接口；调用成本按供应商返回的真实 Token usage 与当前配置单价计量。" />
      <ProCard title="DeepSeek 官方账户" extra={<Button icon={<ReloadOutlined />} loading={billingLoading} onClick={() => void refreshOfficialBilling()}>刷新余额</Button>}>
        {billing?.status === 'available' ? (
          <section className="admin-analysis-cost-summary admin-deepseek-summary">
            <div><span>官方可用余额</span><strong>{balanceText}</strong><small>{billing.isAvailable ? '账户可继续调用' : '余额不足或账户不可用'}</small></div>
            <div><span>{billing.usage.periodDays} 天 DeepSeek 调用</span><strong>{billing.usage.callCount}</strong><small>成功 {billing.usage.successfulCalls} · 分析 {billing.usage.analysisCount}</small></div>
            <div><span>用量计费成本</span><strong>{formatEstimatedCost(billing.usage.meteredCostMicros)}</strong><small>识别 {formatEstimatedCost(billing.usage.recognitionCostMicros)} · 报告 {formatEstimatedCost(billing.usage.reportCostMicros)}</small></div>
            <div><span>平均每次分析</span><strong>{formatEstimatedCost(billing.usage.averageCostPerAnalysisMicros)}</strong><small>按包含 DeepSeek 调用的分析任务计算</small></div>
          </section>
        ) : <Alert type="warning" showIcon message={billing?.error ?? (billing?.status === 'unconfigured' ? '尚未配置 DeepSeek API Key' : '当前模型地址不是 DeepSeek 官方 API，无法查询官方余额')} />}
        {billing ? <div className="admin-billing-footnote">官方余额同步：{formatDate(billing.fetchedAt)} · 缓存命中输入 Token：{billing.usage.cachedInputTokens.toLocaleString('zh-CN')}</div> : null}
      </ProCard>
      <section className="admin-analysis-cost-summary">
        <div><span>分析任务</span><strong>{runs.length}</strong><small>已完成 {completed}</small></div>
        <div><span>累计 Token</span><strong>{totalTokens.toLocaleString('zh-CN')}</strong><small>模型回传用量</small></div>
        <div><span>全部供应商计量成本</span><strong>{formatEstimatedCost(totalCost)}</strong><small>按真实调用用量与配置单价</small></div>
      </section>
      <ProTable<AnalysisRunCost> headerTitle="单次分析成本" rowKey="id" columns={columns} dataSource={runs} search={false} options={false} pagination={{ pageSize: 20 }} scroll={{ x: 1280 }} />
      <Modal width={1120} title={`分析成本明细${detail ? ` · ${detail.id.slice(0, 8)}` : ''}`} open={Boolean(detail)} footer={null} onCancel={() => setDetail(null)}>
        {detail ? (
          <>
            {detail.error ? <Alert type="error" message={detail.error} showIcon className="admin-error" /> : null}
            <ProTable<AnalysisUsageDetail> rowKey="id" columns={usageColumns} dataSource={detail.usage ?? []} search={false} options={false} pagination={false} scroll={{ x: 1050 }} />
          </>
        ) : null}
      </Modal>
    </div>
  );
}

function SettingsPanel({ settings, onSave, onRestore }: {
  settings: RuntimeSettingSummary[];
  onSave: (key: string, value: string) => Promise<void>;
  onRestore: (key: string) => Promise<void>;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const groups = settings.reduce<Record<string, RuntimeSettingSummary[]>>((result, setting) => {
    (result[setting.group] ??= []).push(setting);
    return result;
  }, {});
  const save = async (setting: RuntimeSettingSummary) => {
    const value = drafts[setting.key]?.trim();
    if (!value) return;
    setSavingKey(setting.key);
    try {
      await onSave(setting.key, value);
      setDrafts(current => ({ ...current, [setting.key]: '' }));
    } catch {
      // The parent reports the request error in the page alert. Keep the draft so
      // the operator can retry without exposing an unhandled promise rejection.
    } finally { setSavingKey(null); }
  };
  return (
    <div className="admin-section-stack">
      <Alert className="admin-note" type="warning" showIcon={false} message="动态配置依赖 SETTINGS_ENCRYPTION_KEY。Sentry DSN、管理员密码、数据库密码和主加密密钥应保留在部署环境变量中；后端监控配置修改后需要重启服务。" />
      {Object.entries(groups).map(([group, items]) => (
        <ProCard className="admin-settings-group" key={group} title={group} extra={`${items.filter(item => item.configured).length} / ${items.length} 已配置`}>
          <div className="admin-settings-list">
            {items.map(setting => (
              <article className="admin-setting-row" key={setting.key}>
                <div className="admin-setting-copy"><h3>{setting.label}</h3><p>{setting.description}</p><div><code>{setting.key}</code><Tag color={setting.source === 'database' ? 'processing' : setting.source === 'environment' ? 'success' : 'default'}>{setting.source === 'database' ? '动态覆盖' : setting.source === 'environment' ? '环境变量' : '未配置'}</Tag>{setting.currentValue && <span>{setting.currentValue}</span>}</div></div>
                <div className="admin-setting-editor">
                  {setting.multiline ? <Input.TextArea rows={3} value={drafts[setting.key] ?? ''} onChange={event => setDrafts(current => ({ ...current, [setting.key]: event.target.value }))} placeholder="粘贴新的配置值" /> : setting.secret ? <Input.Password value={drafts[setting.key] ?? ''} onChange={event => setDrafts(current => ({ ...current, [setting.key]: event.target.value }))} placeholder="输入新的配置值" /> : <Input value={drafts[setting.key] ?? ''} onChange={event => setDrafts(current => ({ ...current, [setting.key]: event.target.value }))} placeholder={setting.currentValue ?? '输入配置值'} />}
                  <div><Button type="primary" disabled={!drafts[setting.key]?.trim()} loading={savingKey === setting.key} onClick={() => void save(setting)}>保存并生效</Button>{setting.source === 'database' && <Popconfirm title="恢复环境变量配置？" description="数据库中的动态覆盖值会被删除。" okText="恢复" cancelText="取消" onConfirm={() => onRestore(setting.key).catch(() => undefined)}><Button>恢复默认</Button></Popconfirm>}</div>
                </div>
              </article>
            ))}
          </div>
        </ProCard>
      ))}
    </div>
  );
}

function ConsultationsPanel({ leads, updating, onStatusChange }: {
  leads: ConsultationLead[];
  updating: string | null;
  onStatusChange: (lead: ConsultationLead, status: ConsultationStatus) => Promise<void>;
}) {
  const columns = useMemo<ProColumns<ConsultationLead>[]>(() => [
    {
      title: '客户', dataIndex: 'name', width: 190,
      render: (_, row) => <div><b>{row.name}</b>{row.plusPriority ? <Tag color="gold">Plus 优先</Tag> : null}<small className="admin-table-sub">{row.contact}</small></div>,
    },
    {
      title: '需求', dataIndex: 'topic', width: 300,
      render: (_, row) => <div><span>{row.topic}</span><small className="admin-table-sub">{row.city || '城市未填写'} · {row.preferredTime || '联系时间未填写'}</small></div>,
    },
    {
      title: '关联报告', dataIndex: 'analysisRunId', width: 190,
      render: (_, row) => <span className="admin-mono" title={row.analysisRunId}>{row.analysisRunId.slice(0, 12)}</span>,
    },
    { title: '提交时间', dataIndex: 'createdAt', width: 175, render: (_, row) => formatDate(row.createdAt) },
    {
      title: '状态', dataIndex: 'status', width: 145,
      render: (_, row) => <Tag color={consultationStatusMeta[row.status].color}>{consultationStatusMeta[row.status].label}</Tag>,
    },
    {
      title: '跟进操作', key: 'actions', width: 170,
      render: (_: unknown, row) => (
        <Select
          aria-label={`更新${row.name}的线索状态`}
          value={row.status}
          loading={updating === `consultation:${row.id}`}
          options={(Object.entries(consultationStatusMeta) as Array<[ConsultationStatus, { label: string }]>).map(([value, meta]) => ({ value, label: meta.label }))}
          onChange={status => void onStatusChange(row, status)}
          style={{ width: 138 }}
        />
      ),
    },
  ], [onStatusChange, updating]);

  return (
    <div className="admin-section-stack">
      <Alert type="info" showIcon message="这里展示用户已明确同意联系后提交的咨询需求；有效 Plus 会员会优先排序并标记，请按会员咨询优惠与优先预约规则跟进。" />
      <ProTable<ConsultationLead>
        headerTitle={`专业咨询线索 · ${leads.length}`}
        rowKey="id"
        columns={columns}
        dataSource={leads}
        search={false}
        options={false}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1170 }}
      />
    </div>
  );
}

function UsersPanel({ users, updating, onGrantCredits, onToggleDisabled, onSetGiftPlus }: {
  users: AdminUserRow[];
  updating: string | null;
  onGrantCredits: (user: AdminUserRow, amount: number, reason: string) => Promise<void>;
  onToggleDisabled: (user: AdminUserRow, disabled: boolean) => Promise<void>;
  onSetGiftPlus: (user: AdminUserRow, expiresAt: string | null, reason: string) => Promise<void>;
}) {
  const [giftUser, setGiftUser] = useState<AdminUserRow | null>(null);
  const [giftDays, setGiftDays] = useState(365);
  const [giftReason, setGiftReason] = useState('');
  const [revokeGift, setRevokeGift] = useState(false);
  const [giftSubmitting, setGiftSubmitting] = useState(false);
  const giftSubmitLock = useRef(false);
  const [giftError, setGiftError] = useState('');
  const [creditUser, setCreditUser] = useState<AdminUserRow | null>(null);
  const [creditAmount, setCreditAmount] = useState(5);
  const [reduceCredits, setReduceCredits] = useState(false);
  const [creditReason, setCreditReason] = useState('');
  const [creditError, setCreditError] = useState('');
  const [creditSubmitting, setCreditSubmitting] = useState(false);
  const creditSubmitLock = useRef(false);
  const [keyword, setKeyword] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const filteredUsers = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase();
    return users.filter(user => {
      const matchesKeyword = !normalizedKeyword || [
        user.displayName, user.phoneNumber, user.accountReference, user.id,
      ].some(value => value?.toLocaleLowerCase().includes(normalizedKeyword));
      const matchesSource = sourceFilter === 'all' || user.registrationSource === sourceFilter;
      const matchesRegion = regionFilter === 'all'
        || (regionFilter === 'unknown' ? !user.registrationRegion : user.registrationRegion === regionFilter);
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'disabled' ? Boolean(user.disabledAt) : !user.disabledAt);
      return matchesKeyword && matchesSource && matchesRegion && matchesStatus;
    });
  }, [keyword, regionFilter, sourceFilter, statusFilter, users]);
  const columns = useMemo<ProColumns<AdminUserRow>[]>(() => [
    {
      title: '用户', dataIndex: 'displayName', width: 220,
      render: (_, row) => <div><b>{row.displayName}</b><small className="admin-table-sub">{row.accountReference || (row.phoneNumber ? `${row.countryCode ?? ''} ${row.phoneNumber}`.trim() : '未提供账号信息')}</small></div>,
    },
    {
      title: '注册来源', dataIndex: 'registrationSource', width: 130,
      render: (_, row) => <div><Tag>{userSourceLabels[row.registrationSource ?? ''] ?? '未知'}</Tag>{(row.identityProviders ?? []).length > 1 ? <small className="admin-table-sub">另绑定 {row.identityProviders.slice(1).map(provider => userSourceLabels[provider]).join('、')}</small> : null}</div>,
    },
    {
      title: '注册地区', dataIndex: 'registrationRegion', width: 100,
      render: (_, row) => userRegionLabels[row.registrationRegion ?? ''] ?? '未知',
    },
    {
      title: '状态', dataIndex: 'disabledAt', width: 100,
      render: (_, row) => row.disabledAt ? <Tag color="error">已禁用</Tag> : <Tag color="success">正常</Tag>,
    },
    {
      title: '会员', dataIndex: 'subscriptionActive', width: 105,
      render: (_, row) => row.lifetimeVipActive
        ? <Tag color="gold">历史永久 Plus</Tag>
        : row.subscriptionActive
        ? <Tag color="processing">{row.subscriptionProductId === 'com.guanzhai.ai.plus' ? 'Plus 年度会员' : '旧版订阅会员'}</Tag>
        : row.giftPlusActive ? <Tag color="purple">赠送 Plus</Tag>
        : row.subscriptionExpiresAt || row.giftPlusExpiresAt ? <Tag>已过期</Tag> : <Tag>普通用户</Tag>,
    },
    {
      title: '订阅信息', dataIndex: 'subscriptionProductId', width: 210,
      render: (_, row) => <div>{row.lifetimeVipProductId ? (
        <div><span className="admin-mono">{row.lifetimeVipProductId}</span><small className="admin-table-sub">购买：{formatDate(row.lifetimeVipPurchasedAt)}</small></div>
      ) : row.subscriptionProductId ? (
        <div><span className="admin-mono">{row.subscriptionProductId}</span><small className="admin-table-sub">到期：{formatDate(row.subscriptionExpiresAt)}</small></div>
      ) : row.giftPlusActive ? null : '无商店会员权益'}{row.giftPlusExpiresAt ? <small className="admin-table-sub">赠送 Plus{row.giftPlusActive ? '' : '（已过期）'}：{formatDate(row.giftPlusExpiresAt)}</small> : null}</div>,
    },
    {
      title: '使用次数', dataIndex: 'creditsRemaining', width: 120,
      render: (_, row) => <div><b>剩余 {row.creditsRemaining}</b><small className="admin-table-sub">已消费 {row.consumedAnalysisCount} · 订单 {row.purchaseCount}</small></div>,
    },
    { title: '档案数', dataIndex: 'reportCount', width: 85 },
    { title: '注册时间', dataIndex: 'createdAt', render: (_, row) => formatDate(row.createdAt), width: 175 },
    { title: '最近登录', dataIndex: 'lastLoginAt', render: (_, row) => formatDate(row.lastLoginAt), width: 175 },
    {
      title: '操作', key: 'actions', width: 240,
      render: (_: unknown, row: AdminUserRow) => (
        <div className="admin-user-actions">
          <Button size="small" disabled={Boolean(updating)} onClick={() => { setCreditAmount(5); setReduceCredits(false); setCreditReason(''); setCreditError(''); setCreditUser(row); }}>增加次数</Button>
          <Button size="small" danger disabled={Boolean(updating) || row.creditsRemaining <= 0} onClick={() => { setCreditAmount(1); setReduceCredits(true); setCreditReason(''); setCreditError(''); setCreditUser(row); }}>减少次数</Button>
          <Button size="small" disabled={Boolean(updating)} onClick={() => { setGiftUser(row); setGiftDays(365); setGiftReason(''); setRevokeGift(false); setGiftError(''); }}>开通／延长 VIP</Button>
          <Button size="small" danger title={row.giftPlusActive ? '撤销后台赠送的 VIP；保留 Apple 购买权益' : '当前无有效后台赠送可撤销；Apple 购买权益由商店管理'} disabled={Boolean(updating) || !row.giftPlusActive} onClick={() => { setGiftUser(row); setGiftReason(''); setRevokeGift(true); setGiftError(''); }}>降级 VIP（撤销赠送）</Button>
          {row.disabledAt ? (
            <Popconfirm title="恢复该账号？" description="恢复后用户可以重新登录并使用户型检测。" okText="恢复" cancelText="取消" onConfirm={() => onToggleDisabled(row, false)}>
              <Button size="small" loading={updating === `status:${row.id}`}>恢复账号</Button>
            </Popconfirm>
          ) : (
            <Popconfirm title="禁用该账号？" description="现有登录状态会立即失效，所有分析接口将拒绝访问。" okText="禁用" cancelText="取消" okButtonProps={{ danger: true }} onConfirm={() => onToggleDisabled(row, true)}>
              <Button danger size="small" loading={updating === `status:${row.id}`}>禁用账号</Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ], [onToggleDisabled, updating]);

  const submitCredits = async () => {
    if (!creditUser || creditSubmitLock.current) return;
    if (!creditReason.trim()) { setCreditError('请填写操作原因。'); return; }
    if (!Number.isInteger(creditAmount) || creditAmount < 1 || creditAmount > 10000 || (reduceCredits && creditAmount > creditUser.creditsRemaining)) {
      setCreditError('请填写有效次数，减少次数不能超过可用余额。'); return;
    }
    creditSubmitLock.current = true; setCreditSubmitting(true); setCreditError('');
    try { await onGrantCredits(creditUser, reduceCredits ? -creditAmount : creditAmount, creditReason.trim()); setCreditUser(null); }
    catch { setCreditError('调整失败；若余额已变化，请关闭并刷新后重新确认。'); }
    finally { creditSubmitLock.current = false; setCreditSubmitting(false); }
  };

  return (
    <div className="admin-section-stack">
      <ProCard className="admin-user-filter-card">
        <div className="admin-user-filters">
          <Input.Search allowClear value={keyword} onChange={event => setKeyword(event.target.value)} placeholder="搜索姓名、手机号、邮箱或用户编号" />
          <Select value={sourceFilter} onChange={setSourceFilter} options={[
            { value: 'all', label: '全部来源' },
            ...Object.entries(userSourceLabels).map(([value, label]) => ({ value, label })),
          ]} />
          <Select value={regionFilter} onChange={setRegionFilter} options={[
            { value: 'all', label: '全部地区' },
            ...Object.entries(userRegionLabels).map(([value, label]) => ({ value, label })),
            { value: 'unknown', label: '未知地区' },
          ]} />
          <Select value={statusFilter} onChange={setStatusFilter} options={[
            { value: 'all', label: '全部状态' },
            { value: 'enabled', label: '正常' },
            { value: 'disabled', label: '已禁用' },
          ]} />
          <span className="admin-user-filter-count">显示 {filteredUsers.length} / {users.length} 位用户</span>
        </div>
      </ProCard>
      <ProTable<AdminUserRow> headerTitle="用户账号与会员权益" rowKey="id" columns={columns} dataSource={filteredUsers} search={false} options={false} pagination={{ pageSize: 20 }} scroll={{ x: 1740 }} rowClassName={row => row.disabledAt ? 'admin-user-disabled' : ''} />
      <Modal
        title={`${reduceCredits ? '减少' : '增加'}使用次数${creditUser ? ` · ${creditUser.displayName}` : ''}`}
        open={Boolean(creditUser)}
        okText={reduceCredits ? '确认减少' : '确认增加'}
        cancelText="取消"
        confirmLoading={creditSubmitting}
        closable={!creditSubmitting} maskClosable={!creditSubmitting}
        cancelButtonProps={{ disabled: creditSubmitting }} okButtonProps={{ danger: reduceCredits }}
        onCancel={() => { if (!creditSubmitting) setCreditUser(null); }}
        onOk={submitCredits}
      >
        <p>用户：{creditUser?.displayName}（{creditUser?.id}）</p>
        <p>当前剩余 {creditUser?.creditsRemaining} 次，调整后 {(creditUser?.creditsRemaining ?? 0) + (reduceCredits ? -creditAmount : creditAmount)} 次。只调整完整分析余额，不改购买记录、消费记录或免费识别资格。</p>
        <InputNumber min={1} max={reduceCredits ? Math.min(10000, creditUser?.creditsRemaining ?? 0) : 10000} precision={0} value={creditAmount} onChange={value => setCreditAmount(value ?? 1)} style={{ width: '100%' }} />
        <p>操作原因（必填）</p>
        <Input.TextArea maxLength={200} showCount value={creditReason} onChange={event => setCreditReason(event.target.value)} placeholder="例如：误发回收、客服补偿；不要填写敏感个人资料" />
        {creditError ? <Alert type="error" message={creditError} /> : null}
      </Modal>
      <Modal title={revokeGift ? '撤销赠送 Plus' : '赠送 Plus'} open={Boolean(giftUser)}
        okText={revokeGift ? '确认撤销赠送' : '确认赠送'} cancelText="取消"
        confirmLoading={giftSubmitting} closable={!giftSubmitting} maskClosable={!giftSubmitting}
        cancelButtonProps={{ disabled: giftSubmitting }}
        onCancel={() => { if (!giftSubmitting) setGiftUser(null); }}
        onOk={async () => {
          if (!giftUser || giftSubmitLock.current) return;
          if (!giftReason.trim()) { setGiftError('请填写操作原因，不要填写个人敏感资料。'); return; }
          giftSubmitLock.current = true; setGiftSubmitting(true); setGiftError('');
          try {
            const start = Math.max(Date.now(), Date.parse(giftUser.giftPlusExpiresAt ?? '') || 0);
            await onSetGiftPlus(giftUser, revokeGift ? null : new Date(start + giftDays * 86400000).toISOString(), giftReason.trim());
            setGiftUser(null);
          } catch { setGiftError('保存失败，请核对期限或稍后重试。'); }
          finally { giftSubmitLock.current = false; setGiftSubmitting(false); }
        }}>
        <p>用户：{giftUser?.displayName || giftUser?.phoneNumber}（{giftUser?.id}）</p>
        <Alert type="info" showIcon message={revokeGift ? '只撤销赠送权益，不影响 Apple 订阅或历史永久权益。' : '从现有赠送到期日或当前时间延长；最长累计三年。不赠送分析次数，不生成 Apple 订单或自动续费。'} />
        {!revokeGift ? <p>赠送天数 <InputNumber min={1} max={1095} precision={0} value={giftDays} onChange={value => setGiftDays(value ?? 1)} /></p> : null}
        <p>操作原因（必填）</p>
        <Input.TextArea maxLength={200} showCount value={giftReason} onChange={event => setGiftReason(event.target.value)} placeholder="例如：活动赠送、客服补偿；不要填写敏感个人资料" />
        {giftError ? <Alert type="error" message={giftError} /> : null}
      </Modal>
    </div>
  );
}

function AdminConsole({ session, onLogout }: { session: AdminSession; onLogout: () => void }) {
  const initialSection = window.location.hash.replace('#/', '').replace('#', '');
  const [section, setSection] = useState<Section>(isSection(initialSection) ? initialSection : 'overview');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [features, setFeatures] = useState<FeatureFlag[]>([]);
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [consultations, setConsultations] = useState<ConsultationLead[]>([]);
  const [settings, setSettings] = useState<RuntimeSettingSummary[]>([]);
  const [finance, setFinance] = useState<AppleFinanceData | null>(null);
  const [appleTransactions, setAppleTransactions] = useState<AppleTransaction[]>([]);
  const [appleIssues, setAppleIssues] = useState<AppleReconciliationIssue[]>([]);
  const [analysisRuns, setAnalysisRuns] = useState<AnalysisRunCost[]>([]);
  const [deepSeekBilling, setDeepSeekBilling] = useState<DeepSeekBillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshingOrders, setRefreshingOrders] = useState(false);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dashboardData, featureData, eventData, userData, consultationData, settingData, financeData, transactionData, issueData, analysisRunData, billingData] = await Promise.all([
        fetchAdminDashboard(session.token),
        fetchFeatureFlags(session.token),
        fetchAnalyticsEvents(session.token),
        fetchAdminUsers(session.token),
        fetchConsultationLeads(session.token),
        fetchRuntimeSettings(session.token),
        fetchAppleFinance(session.token),
        fetchAppleTransactions(session.token),
        fetchAppleReconciliationIssues(session.token),
        fetchAnalysisRuns(session.token),
        fetchDeepSeekBilling(session.token),
      ]);
      setDashboard(dashboardData); setFeatures(featureData); setEvents(eventData); setUsers(userData);
      setConsultations(consultationData);
      setSettings(settingData); setFinance(financeData); setAppleTransactions(transactionData);
      setAppleIssues(issueData);
      setAnalysisRuns(analysisRunData);
      setDeepSeekBilling(billingData);
    } catch (requestError) {
      if (axios.isAxiosError(requestError) && requestError.response?.status === 401) return onLogout();
      setError('后台数据加载失败，请检查后端服务状态后重试。');
    } finally { setLoading(false); }
  }, [onLogout, session.token]);

  useEffect(() => { void load(); }, [load]);

  const toggleFeature = async (feature: FeatureFlag, enabled: boolean) => {
    setUpdating(feature.key); setError('');
    try {
      const updated = await updateFeatureFlag(session.token, feature.key, enabled);
      setFeatures(items => items.map(item => item.key === updated.key ? updated : item));
      const [dashboardData, eventData] = await Promise.all([fetchAdminDashboard(session.token), fetchAnalyticsEvents(session.token)]);
      setDashboard(dashboardData); setEvents(eventData);
    } catch { setError(`${feature.name}状态更新失败，原状态未改变。`); }
    finally { setUpdating(null); }
  };

  const saveSetting = async (key: string, value: string) => {
    setError('');
    try {
      const updated = await updateRuntimeSetting(session.token, key, value);
      setSettings(items => items.map(item => item.key === key ? updated : item));
    } catch (requestError) {
      const message = axios.isAxiosError(requestError) ? requestError.response?.data?.message : undefined;
      setError(String(message ?? '配置保存失败，现有运行配置未改变。'));
      throw requestError;
    }
  };

  const restoreSetting = async (key: string) => {
    setError('');
    try {
      const updated = await restoreRuntimeSetting(session.token, key);
      setSettings(items => items.map(item => item.key === key ? updated : item));
    } catch (requestError) {
      setError('恢复环境变量配置失败。');
      throw requestError;
    }
  };

  const saveSettlement = async (values: { fiscalPeriod: string; currency: string; proceedsAmount: number; paidAmount?: number }) => {
    setError('');
    try {
      const updated = await saveAppleSettlement(session.token, values);
      setFinance(updated);
    } catch (requestError) {
      setError('App Store 结算记录保存失败。');
      throw requestError;
    }
  };

  const refreshOrders = async () => {
    setRefreshingOrders(true);
    setError('');
    try {
      const result = await refreshAppleTransactions(session.token);
      setAppleTransactions(result.transactions);
      const [financeData, userData, issueData] = await Promise.all([
        fetchAppleFinance(session.token),
        fetchAdminUsers(session.token),
        fetchAppleReconciliationIssues(session.token),
      ]);
      setFinance(financeData);
      setUsers(userData);
      setAppleIssues(issueData);
      if (result.failedCount > 0 || result.notificationFailedCount > 0) {
        setError(`App Store 已完成部分同步：订单历史失败 ${result.failedCount}，通知历史失败 ${result.notificationFailedCount}。`);
      }
    } catch (requestError) {
      const message = axios.isAxiosError(requestError) ? requestError.response?.data?.message : undefined;
      setError(String(message ?? 'App Store 订单刷新失败，请检查 Server API 凭据。'));
      throw requestError;
    } finally {
      setRefreshingOrders(false);
    }
  };

  const lookupOrder = useCallback((orderId: string) => (
    lookupAppleOrder(session.token, orderId)
  ), [session.token]);

  const loadAnalysisRunDetail = useCallback((id: string) => (
    fetchAnalysisRunDetail(session.token, id)
  ), [session.token]);

  const refreshDeepSeekBilling = useCallback(async () => {
    setDeepSeekBilling(await fetchDeepSeekBilling(session.token));
  }, [session.token]);

  const reconcileOrder = useCallback(async (input: {
    orderId: string;
    transactionId: string;
    userId?: string;
    confirmManualAssignment?: boolean;
  }) => {
    setError('');
    const result = await reconcileAppleOrder(session.token, input);
    const [transactionData, issueData, userData, financeData] = await Promise.all([
      fetchAppleTransactions(session.token),
      fetchAppleReconciliationIssues(session.token),
      fetchAdminUsers(session.token),
      fetchAppleFinance(session.token),
    ]);
    setAppleTransactions(transactionData);
    setAppleIssues(issueData);
    setUsers(userData);
    setFinance(financeData);
    return result.order;
  }, [session.token]);

  const grantCredits = useCallback(async (user: AdminUserRow, amount: number, reason: string) => {
    setUpdating(`credits:${user.id}`); setError('');
    try {
      const updated = await grantAdminUserCredits(session.token, user.id, amount, reason, user.creditsRemaining);
      setUsers(items => items.map(item => item.id === updated.id ? updated : item));
    } catch (requestError) {
      const message = axios.isAxiosError(requestError) ? requestError.response?.data?.message : undefined;
      setError(String(message ?? '调整使用次数失败。'));
      throw requestError;
    } finally { setUpdating(null); }
  }, [session.token]);

  const toggleUserDisabled = useCallback(async (user: AdminUserRow, disabled: boolean) => {
    setUpdating(`status:${user.id}`); setError('');
    try {
      const updated = await updateAdminUserStatus(session.token, user.id, disabled, disabled ? '管理员禁用' : undefined);
      setUsers(items => items.map(item => item.id === updated.id ? updated : item));
    } catch (requestError) {
      const message = axios.isAxiosError(requestError) ? requestError.response?.data?.message : undefined;
      setError(String(message ?? `${disabled ? '禁用' : '恢复'}账号失败。`));
      throw requestError;
    } finally { setUpdating(null); }
  }, [session.token]);

  const setGiftPlus = useCallback(async (user: AdminUserRow, expiresAt: string | null, reason: string) => {
    setUpdating(`gift:${user.id}`); setError('');
    try {
      const updated = await setAdminUserGiftPlus(session.token, user.id, expiresAt, reason);
      setUsers(items => items.map(item => item.id === updated.id ? { ...item, ...updated } : item));
    } catch (requestError) {
      const message = axios.isAxiosError(requestError) ? requestError.response?.data?.message : undefined;
      setError(String(message ?? '更新赠送 Plus 失败。'));
      throw requestError;
    } finally { setUpdating(null); }
  }, [session.token]);

  const changeConsultationStatus = useCallback(async (lead: ConsultationLead, status: ConsultationStatus) => {
    setUpdating(`consultation:${lead.id}`); setError('');
    try {
      const updated = await updateConsultationLeadStatus(session.token, lead.id, status);
      setConsultations(items => items.map(item => item.id === updated.id ? updated : item));
    } catch (requestError) {
      const message = axios.isAxiosError(requestError) ? requestError.response?.data?.message : undefined;
      setError(String(message ?? '咨询线索状态更新失败。'));
      throw requestError;
    } finally { setUpdating(null); }
  }, [session.token]);

  const eventColumns = useMemo<ProColumns<AnalyticsEvent>[]>(() => [
    { title: '事件', dataIndex: 'eventName', render: (_, row) => eventLabels[row.eventName] ?? row.eventName },
    { title: '状态', dataIndex: 'success', render: (_, row) => <Tag color={row.success ? 'success' : 'error'}>{row.success ? '成功' : '异常'}</Tag> },
    { title: '耗时', dataIndex: 'durationMs', render: (_, row) => row.durationMs === undefined || row.durationMs === null ? '—' : `${row.durationMs} ms` },
    { title: '发生时间', dataIndex: 'createdAt', render: (_, row) => formatDate(row.createdAt) },
  ], []);

  const navigate = (path?: string) => {
    const next = path?.replace(/^\//, '') ?? '';
    if (!isSection(next)) return;
    window.history.replaceState(null, '', `#/${next}`);
    setSection(next);
  };

  const accountMenu = {
    items: [
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: onLogout },
    ],
  };

  const currentPage = sectionMeta[section];
  return (
    <ProLayout
      className="admin-pro-shell"
      title="观宅运营台"
      logo={<span className="admin-layout-logo">宅</span>}
      layout="mix"
      navTheme="light"
      siderWidth={236}
      fixedHeader
      fixSiderbar
      location={{ pathname: `/${section}` }}
      route={{ path: '/', routes: proRoutes }}
      menuItemRender={(item, dom) => <a href={`#${item.path}`} onClick={event => { event.preventDefault(); navigate(item.path); }}>{dom}</a>}
      actionsRender={() => [
        <Button key="refresh" type="text" icon={<ReloadOutlined />} onClick={() => void load()}>刷新</Button>,
      ]}
      avatarProps={{
        title: session.user.username,
        render: (_props, avatar) => <Dropdown menu={accountMenu} trigger={['click']}>{avatar}</Dropdown>,
      }}
      menuFooterRender={props => props?.collapsed ? null : (
        <div className="admin-pro-menu-foot"><span>PRODUCTION READY</span><small>数据来自当前部署环境</small></div>
      )}
      token={{
        header: {
          colorBgHeader: '#fffdf9',
          colorHeaderTitle: '#18201c',
          colorTextMenu: '#3f4943',
          colorTextMenuSelected: '#ad432d',
        },
        sider: {
          colorMenuBackground: '#18201c',
          colorTextMenu: '#d6ddd7',
          colorTextMenuSelected: '#ffffff',
          colorBgMenuItemSelected: '#b84b33',
          colorBgMenuItemHover: '#2a352f',
        },
      }}
    >
      <PageContainer
        className="admin-page-container"
        title={currentPage.title}
        subTitle={currentPage.description}
        breadcrumb={{ items: [{ title: '运营后台' }, { title: currentPage.title }] }}
        extra={[<Tag color="success" key="sync">数据已同步</Tag>]}
      >
          {error && <Alert className="admin-error" type="error" message={error} closable onClose={() => setError('')} showIcon={false} />}
          {loading ? <ProCard className="admin-loading"><Skeleton active paragraph={{ rows: 8 }} /></ProCard> : (
            <>
              {section === 'overview' && dashboard && <Overview dashboard={dashboard} />}
              {section === 'analysis-costs' && <AnalysisCostsPanel runs={analysisRuns} billing={deepSeekBilling} loadDetail={loadAnalysisRunDetail} refreshBilling={refreshDeepSeekBilling} />}
              {section === 'orders' && <OrdersPanel transactions={appleTransactions} issues={appleIssues} users={users} refreshing={refreshingOrders} onRefresh={refreshOrders} onLookupOrder={lookupOrder} onReconcileOrder={reconcileOrder} />}
              {section === 'finance' && finance && <FinancePanel finance={finance} onSaveSettlement={saveSettlement} />}
              {section === 'features' && <FeaturePanel features={features} updating={updating} onToggle={toggleFeature} />}
              {section === 'users' && <UsersPanel users={users} updating={updating} onGrantCredits={grantCredits} onToggleDisabled={toggleUserDisabled} onSetGiftPlus={setGiftPlus} />}
              {section === 'consultations' && <ConsultationsPanel leads={consultations} updating={updating} onStatusChange={changeConsultationStatus} />}
              {section === 'events' && <ProTable<AnalyticsEvent> headerTitle="用户与分析事件" rowKey="id" columns={eventColumns} dataSource={events} search={false} options={false} pagination={{ pageSize: 20 }} scroll={{ x: 620 }} />}
              {section === 'settings' && <SettingsPanel settings={settings} onSave={saveSetting} onRestore={restoreSetting} />}
            </>
          )}
      </PageContainer>
    </ProLayout>
  );
}

export default function AdminApp() {
  const [session, setSession] = useState<AdminSession | null>(() => readSession());
  const logout = useCallback(() => { localStorage.removeItem(SESSION_KEY); setSession(null); }, []);
  return (
    <ConfigProvider theme={{
      token: {
        colorPrimary: '#b84b33',
        colorPrimaryHover: '#9f3e2a',
        colorPrimaryActive: '#833321',
        colorSuccess: '#3f7152',
        colorWarning: '#a56a1e',
        colorError: '#b23b32',
        colorInfo: '#356b83',
        colorText: '#18201c',
        colorTextSecondary: '#59645d',
        colorTextTertiary: '#78827b',
        colorBgLayout: '#f1f3ef',
        colorBgContainer: '#fffdf9',
        colorBorder: '#cfd5cf',
        colorBorderSecondary: '#e1e5e0',
        borderRadius: 7,
        fontFamily: '"Geist", "PingFang SC", "Microsoft YaHei", sans-serif',
      },
      components: {
        Button: { fontWeight: 600 },
        Card: { headerBg: '#fffdf9' },
        Menu: {
          darkItemBg: '#18201c',
          darkItemColor: '#d6ddd7',
          darkItemHoverBg: '#2a352f',
          darkItemHoverColor: '#ffffff',
          darkItemSelectedBg: '#b84b33',
          darkItemSelectedColor: '#ffffff',
          itemBorderRadius: 6,
          itemHeight: 44,
        },
        Table: { headerBg: '#e9ede8', headerColor: '#303a34', rowHoverBg: '#f6f2eb' },
      },
    }}>
      {session ? <AdminConsole session={session} onLogout={logout} /> : <AdminLogin onSuccess={setSession} />}
    </ConfigProvider>
  );
}
